import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, Users, Sparkles, TrendingUp, CheckCircle2, Calendar, DollarSign, AlertTriangle, Download, Leaf } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import AccountabilityPanel from "@/pages/admin/AccountabilityPanel";
import DashboardHero from "@/components/DashboardHero";
import { money } from "@/lib/format";

const TABS = ["Overview", "Bridge the Gaps", "Revenue", "Accountability", "Compliance"];
const sev = { high: "bg-red-100 text-red-600", medium: "bg-[#D98E73]/15 text-[#B5794E]", low: "bg-[#5C8A72]/15 text-[#5C8A72]" };

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="eyw-card p-6">
      <div className="flex items-center justify-between">
        <span className="text-3xl font-serif text-[#144F75]">{value}</span>
        {Icon && <Icon size={20} className="text-[#D98E73]" />}
      </div>
      <p className="text-sm text-[#4A5D6B] mt-1">{label}</p>
    </div>
  );
}

export default function CouncilDashboard() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("Overview");
  const isAdmin = ["admin", "staff"].includes(user?.role);

  const ov = useQuery({ queryKey: ["council-overview"], queryFn: () => api.get("/council/overview").then(r => r.data) });
  const gaps = useQuery({ queryKey: ["council-gaps"], queryFn: () => api.get("/council/gaps").then(r => r.data) });
  const rev = useQuery({ queryKey: ["council-revenue"], queryFn: () => api.get("/revenue").then(r => r.data) });
  const comp = useQuery({ queryKey: ["council-compliance"], queryFn: () => api.get("/compliance/overview").then(r => r.data) });

  const markPaid = async (id) => { await api.post(`/revenue/${id}/payout`); toast.success("Marked as paid"); qc.invalidateQueries({ queryKey: ["council-revenue"] }); };
  const exportCsv = async () => {
    const res = await api.get("/revenue/export.csv", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "eyw-revenue.csv"; a.click(); window.URL.revokeObjectURL(url);
  };

  const o = ov.data || {};
  const summary = rev.data?.summary || {};

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="council-dashboard">
      <DashboardHero label={`${isAdmin ? "Admin" : "Council"} · Ecosystem Overview`} title={`Welcome, ${user?.name || "Council"}.`}
        subtitle="A living overview of the whole ecosystem — members, revenue, gaps, and compliance health."
        onLogout={logout} logoutTestId="council-logout" />

      <div className="flex gap-2 border-b border-[#E5E0D8] mb-8 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`council-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-[#144F75] text-[#144F75] font-semibold" : "border-transparent text-[#4A5D6B] hover:text-[#144F75]"}`}>
            {t}{t === "Bridge the Gaps" && gaps.data?.total ? ` (${gaps.data.total})` : ""}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="council-overview-panel">
          <Stat label="Total Members" value={o.members ?? "—"} icon={Users} />
          <Stat label="Discovery" value={o.discovery ?? "—"} icon={Sparkles} />
          <Stat label="Community" value={o.community ?? "—"} icon={Users} />
          <Stat label="Enhancers" value={o.enhancers ?? "—"} icon={TrendingUp} />
          <Stat label="Pending Enhancer Apps" value={o.pending_enhancer_apps ?? "—"} icon={CheckCircle2} />
          <Stat label="Pending Services" value={o.pending_services ?? "—"} icon={CheckCircle2} />
          <Stat label="Upcoming Events" value={o.upcoming_events ?? "—"} icon={Calendar} />
          <Stat label="Total Bookings" value={o.total_bookings ?? "—"} icon={Calendar} />
          <Stat label="Signed Agreements" value={o.signed_agreements ?? "—"} icon={CheckCircle2} />
          <Stat label="Stewardship Projects" value={o.stewardship_projects ?? "—"} icon={Leaf} />
          <Stat label="Gross Revenue" value={`${money((o.revenue_gross ?? 0))}`} icon={DollarSign} />
          <Stat label="Pending Payouts" value={`${money((o.pending_payouts ?? 0))}`} icon={DollarSign} />
        </div>
      )}

      {tab === "Bridge the Gaps" && (
        <div data-testid="council-gaps-panel">
          <p className="text-[#4A5D6B] text-sm mb-5">Where the ecosystem needs attention — so each person can practice their enhancements.</p>
          {gaps.data?.gaps?.length ? (
            <div className="space-y-3">
              {gaps.data.gaps.map((g) => (
                <div key={g.category} data-testid={`gap-${g.category}`} className="eyw-card p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <AlertTriangle size={18} className={g.severity === "high" ? "text-red-500" : g.severity === "medium" ? "text-[#D98E73]" : "text-[#5C8A72]"} />
                    <div>
                      <div className="text-sm font-semibold text-[#1A242B]">{g.label}</div>
                      <div className="text-xs text-[#4A5D6B]">{g.action}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-serif text-[#144F75]">{g.count}</span>
                    <span className={`text-xs px-2.5 py-1 rounded-full ${sev[g.severity]}`}>{g.severity}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-[#4A5D6B]">Everything is in order — no gaps detected.</p>}
        </div>
      )}

      {tab === "Revenue" && (
        <div data-testid="council-revenue-panel">
          <div className="flex justify-between items-center mb-5">
            <h2 className="font-serif text-2xl text-[#1A242B]">Revenue & payouts</h2>
            <button onClick={exportCsv} data-testid="revenue-export" className="eyw-btn-ghost !py-2 text-sm"><Download size={15} /> Export CSV</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Gross" value={`${money((summary.gross ?? 0))}`} />
            <Stat label="Ecosystem" value={`${money((summary.ecosystem_total ?? 0))}`} />
            <Stat label="Pending Payouts" value={`${money((summary.pending_payouts ?? 0))}`} />
            <Stat label="Paid Out" value={`${money((summary.paid_payouts ?? 0))}`} />
          </div>
          <div className="eyw-card divide-y divide-[#E5E0D8]">
            {(rev.data?.records || []).map((r) => (
              <div key={r.id} className="p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-[#1A242B]">{r.service_title || "Offering"} <span className="text-xs text-[#8FA89B]">· {r.enhancer_name || "EYW"}</span></div>
                  <div className="text-xs text-[#4A5D6B]">Gross {money(r.gross)} · {r.revenue_model} · enhancer {money(r.enhancer_share)} / eco {money(r.ecosystem_share)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full ${r.payout_status === "paid" ? "bg-[#144F75]/10 text-[#144F75]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{r.payout_status}</span>
                  {isAdmin && r.payout_status === "pending" && (
                    <button onClick={() => markPaid(r.id)} data-testid={`payout-${r.id}`} className="eyw-btn !py-1.5 !px-3 text-xs">Mark paid</button>
                  )}
                </div>
              </div>
            ))}
            {rev.data?.records?.length === 0 && <p className="text-[#4A5D6B] text-sm p-4">No revenue yet — records appear after paid bookings.</p>}
          </div>
        </div>
      )}

      {tab === "Accountability" && <AccountabilityPanel />}

      {tab === "Compliance" && (
        <div data-testid="council-compliance-panel">
          <p className="text-[#4A5D6B] text-sm mb-5">Compliance health — agreement completion, privacy requests, verification, and payout readiness.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Stat label="Agreement Completion" value={`${comp.data?.agreement_completion_pct ?? 0}%`} icon={CheckCircle2} />
            <Stat label="Open Data Requests" value={comp.data?.open_data_requests ?? "—"} icon={AlertTriangle} />
            <Stat label="Unapproved Services" value={comp.data?.unapproved_services ?? "—"} icon={AlertTriangle} />
            <Stat label="Unapproved Locations" value={comp.data?.unapproved_locations ?? "—"} icon={AlertTriangle} />
            <Stat label="Unpaid Bookings" value={comp.data?.unpaid_bookings ?? "—"} icon={AlertTriangle} />
            <Stat label="Pending Payouts" value={comp.data?.pending_payouts ?? "—"} icon={DollarSign} />
            <Stat label="Pending Enhancer KYC" value={comp.data?.pending_enhancer_apps ?? "—"} icon={CheckCircle2} />
            <Stat label="Active Agreements" value={comp.data?.active_agreements ?? "—"} icon={CheckCircle2} />
          </div>
          <p className="text-xs text-[#8FA89B] mt-6">Council sees compliance health summaries only — never private wellness records or GAUGE reflections.</p>
        </div>
      )}
    </div>
  );
}
